import { createContext, useContext, useMemo } from "react";
import useLocalStorage from "../hooks/useLocalStorage";
import { useAppDispatch, useAppSelector } from "../store";
import type { Cliente } from "../types/cliente";
import authApi from "../api/auth.api";
import { ACCESS_TOKEN_KEY } from "../helpers/constants";

type AuthProviderValue = {
  cliente: Cliente | null;
  token: string | null;
  login: (cpf: string, password: string, redirectTo?: string) => Promise<void>;
  logout: () => void;
};

export const AuthContext = createContext<AuthProviderValue>({
  cliente: null,
  token: null,
  login: async (_cpf: string, _password: string, _redirectTo?: string) => {},
  logout: () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const { cliente } = useAppSelector((state) => state.cliente);
  const [token, setToken, removeToken] = useLocalStorage(ACCESS_TOKEN_KEY);

  const login = async (
    cpf: string,
    password: string,
    redirectTo = "/app/minha-conta"
  ) => {
    try {
      const { token, cliente } = await authApi.authenticate(cpf, password);

      setToken(token);

      dispatch({ type: "SET_CLIENTE", payload: cliente });

      window.location.href = redirectTo;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const logout = () => {
    if (token) {
      removeToken();
    }

    window.location.href = "/login";
  };

  const value = useMemo<Pick<AuthProviderValue, "cliente" | "token">>(
    () => ({
      cliente,
      token,
    }),
    [cliente, token]
  );

  return (
    <AuthContext.Provider
      value={{ cliente: value?.cliente, token: value?.token, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  return useContext(AuthContext);
};
